import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../CaseResult/Result-p.css";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const Resultp = () => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const overlayRef = useRef(null);
  const headingRef = useRef(null);
  const textRef = useRef(null);
  const stripsContainerRef = useRef(null);
  const stripsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Initial animation timeline
      const tl = gsap.timeline();

      // Animate strips - staggered reveal
      stripsRef.current.forEach((strip, index) => {
        gsap.set(strip, { opacity: 0, scaleY: 0, transformOrigin: "top" });
        tl.to(
          strip,
          {
            opacity: 0.6,
            scaleY: 1,
            duration: 0.6,
            delay: index * 0.03,
            ease: "back.out(0.7)",
          },
          0,
        );
      });

      // Fade in overlay background
      tl.to(
        overlayRef.current,
        {
          opacity: 1,
          duration: 1.2,
          ease: "power2.out",
        },
        "-=0.3",
      );

      // Animate heading with 3D rotation and scale
      tl.fromTo(
        headingRef.current,
        {
          x: -150,
          opacity: 0,
          rotationY: -30,
          scale: 0.8,
        },
        {
          x: 0,
          opacity: 1,
          rotationY: 0,
          scale: 1,
          duration: 1.2,
          delay: 0.2,
          ease: "back.out(0.8)",
        },
      );

      // Animate text with blur effect
      tl.fromTo(
        textRef.current,
        {
          x: -100,
          opacity: 0,
          filter: "blur(10px)",
        },
        {
          x: 0,
          opacity: 1,
          filter: "blur(0px)",
          duration: 1,
          delay: 0.2,
          ease: "power3.out",
        },
      );

      // Image scale animation on scroll
      gsap.fromTo(
        imageRef.current,
        {
          scale: 1,
        },
        {
          scale: 1.08,
          duration: 1.5,
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1,
          },
        },
      );

      // Parallax effect for overlay content
      gsap.to([headingRef.current, textRef.current], {
        y: 60,
        duration: 1,
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 0.8,
        },
      });

      // Hover animations for strips
      stripsRef.current.forEach((strip) => {
        strip.addEventListener("mouseenter", () => {
          gsap.to(strip, {
            opacity: 0.9,
            scaleY: 1.05,
            duration: 0.3,
            ease: "power2.out",
          });
        });
        strip.addEventListener("mouseleave", () => {
          gsap.to(strip, {
            opacity: 0.6,
            scaleY: 1,
            duration: 0.3,
            ease: "power2.out",
          });
        });
      });

      // Floating animation for decorative elements
      gsap.to(".floating-element", {
        y: "+=15",
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: 0.2,
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // Create refs for strips
  const setStripRef = (el, index) => {
    if (el) stripsRef.current[index] = el;
  };

  return (
    <div className="entry-container-ep" ref={containerRef}>
      <div className="entry-image-container-ep">
        <img
          src="/result.webp"
          alt="Inside the Case Study"
          className="entry-image-ep"
          ref={imageRef}
        />

        {/* Animated Strips */}
        <div className="strips-ep" ref={stripsContainerRef}>
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="vertical-strip-ep"
              ref={(el) => setStripRef(el, i)}
            />
          ))}
        </div>

        {/* Overlay Content */}
        <div className="entry-overlay-ep" ref={overlayRef}>
          <div className="overlay-content-wrapper">
            <div className="accent-line" />
            <h1 className="entry-overlay-text-ep" ref={headingRef}>
              Inside the
              <span className="gradient-text"> Case</span>
            </h1>
            <p className="entry-text-ep" ref={textRef}>
              Step behind the scenes to uncover the meticulous legal strategy
              and relentless advocacy that turned challenges into victories.
              Each case tells a story of perseverance, innovation, and justice
              delivered.
            </p>
          </div>
        </div>

        {/* Decorative floating elements */}
        <div className="floating-elements">
          <div className="floating-element element-1" />
          <div className="floating-element element-2" />
          <div className="floating-element element-3" />
        </div>
      </div>
    </div>
  );
};

export default Resultp;
